/**
 * Confirm deleting one workflow (DOP-491).
 *
 * A confirm step, not a toast-with-undo: none of the three backends can put a
 * deleted workflow back, so the only honest place to stop is before.
 *
 * The dialog says what goes and what stays, per kind, because those differ in
 * the way that matters. A KPI workflow's extracted values survive it — they are
 * stamped onto the dashboard rather than joined to the workflow — so deleting
 * one retires the instruction and leaves the numbers. A monitoring workflow's
 * ledgers do not survive: everything hangs off the template, locked runs
 * included. Saying "this cannot be undone" and nothing else would leave the
 * user to guess which of those they are about to do.
 */

import { Loader2 } from "lucide-react";
import React, { useRef } from "react";
import ModalDialog from "../ModalDialog";
import type { DashboardIndexRow, DashboardKind } from "../../context/DashboardsIndex";

/** What the delete takes with it, in the user's terms, per kind. */
const CONSEQUENCE: Record<DashboardKind, string> = {
	kpi: "Its run history goes with it. The metric values it already extracted stay on your dashboard.",
	monitoring:
		"Its borrowers, periods and every generated ledger go with it, including runs that were locked.",
	loan_tape: "The chat it was built from, and the workbooks in it, go with it.",
	deal_screening:
		"Its deals, every memo version they produced, and the values people entered on them go with it. The Deal Room folders and their files stay, and chat threads about its memos stay in Chat History.",
	portfolio_data:
		"Portfolio tables are built from the client's workbooks; they are not deleted from here.",
};

export interface DeleteWorkflowDialogProps {
	row: DashboardIndexRow;
	/** True while the request is in flight; both buttons wait for it. */
	deleting: boolean;
	/** The backend's refusal, shown in the dialog rather than behind it. */
	error: string | null;
	onCancel: () => void;
	onConfirm: () => void;
}

const DeleteWorkflowDialog: React.FC<DeleteWorkflowDialogProps> = ({
	row,
	deleting,
	error,
	onCancel,
	onConfirm,
}) => {
	// Focus lands on Cancel, not on Delete: the safe choice is the one that
	// should be under a reflexive Enter.
	const cancelRef = useRef<HTMLButtonElement>(null);

	return (
		<ModalDialog
			titleId="delete-workflow-title"
			initialFocusRef={cancelRef}
			onClose={deleting ? () => undefined : onCancel}
		>
			<h2
				id="delete-workflow-title"
				className="text-base font-semibold text-text-base"
			>
				Delete “{row.name}”?
			</h2>
			<p className="mt-2 text-sm text-text-muted">{CONSEQUENCE[row.kind]}</p>
			<p className="mt-2 text-sm text-text-muted">This cannot be undone.</p>

			{error && (
				<p role="alert" className="mt-3 text-sm text-red-700">
					{error}
				</p>
			)}

			<div className="mt-5 flex justify-end gap-2">
				<button
					type="button"
					ref={cancelRef}
					onClick={onCancel}
					disabled={deleting}
					className="rounded border border-gray-200 px-3 py-1.5 text-sm font-medium text-text-base hover:bg-gray-50 disabled:opacity-60"
				>
					Cancel
				</button>
				<button
					type="button"
					onClick={onConfirm}
					disabled={deleting}
					className="inline-flex items-center gap-2 rounded bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-60"
				>
					{deleting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
					Delete
				</button>
			</div>
		</ModalDialog>
	);
};

export default DeleteWorkflowDialog;
